import { createSelector } from '@reduxjs/toolkit'

import { IApplicationState } from '@store'
import { selectCampaign } from '@store/campaigns'

// TODO(BRAND-9275): Confirm rel value with Platform once endpoint is built.
const VIBE_CHECK_LIST_REL = 'creator-search-vibe-check'

// TODO(BRAND-9275): remove fallback once Platform exposes sort_by on the link.
const FALLBACK_SORT_OPTIONS = ['score', 'name', 'audience_size']

const selectAgentSourcedCreatorsSlice = (state: IApplicationState) => state.agentSourcedCreators

export const selectAgentSourcedCreators = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.creators,
)

export const selectAgentSourcedCreatorsCollectionLinks = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.collectionLinks,
)

export const selectAgentSourcedCreatorsLoading = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.loading,
)

export const selectAgentSourcedCreatorsError = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.error,
)

export const selectAgentSourcedCreatorActionLoading = (creatorId: number) =>
  createSelector(selectAgentSourcedCreatorsSlice, (s) => s.actionLoading[creatorId] ?? false)

export const selectAgentSourcedCreatorsListing = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.listing,
)

// Sticky per-listing totals. Each only updates from its matching fetch and
// persists across tab switches, so the sidebar badge reflects the last known
// count for every tab independently.
export const selectAgentSourcedCreatorsTotal = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.vibeCheckTotal,
)

export const selectInterestedCount = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.interestedTotal,
)

export const selectShortlistShortlistedCount = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.shortlistedTotal,
)

// BRAND-9306 run-aware review views. Contract provisional pending BRAND-9510
// (BE) — re-verify field/param names once that PR merges.
export const selectSelectedRunScope = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.selectedRunScope,
)

export const selectSourcingRuns = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.sourcingRuns,
)

// BRAND-9514 bulk reject: capability is link-driven — the BE offers
// `bulk_reject` on the COLLECTION response only while the run is decidable
// (ready_for_vibe_check with >=1 pending). Link presence IS the permission.
const BULK_REJECT_REL = 'bulk_reject'

export const selectBulkRejectLink = createSelector(
  selectAgentSourcedCreatorsCollectionLinks,
  (links) => links.find((l) => l.rel === BULK_REJECT_REL),
)

export const selectBulkRejectStatus = createSelector(
  selectAgentSourcedCreatorsSlice,
  (s) => s.bulkRejectStatus,
)

const DOWNLOAD_CSV_REL = 'download_csv'

// Emitted by Platform for tribe staff only, and only when the shortlisted
// bucket is non-empty (BRAND-9636). The link's presence IS the permission
// check — do not re-derive it client-side.
export const selectDownloadShortlistCsvLink = createSelector(
  selectAgentSourcedCreatorsCollectionLinks,
  (links) => links.find((l) => l.rel === DOWNLOAD_CSV_REL),
)

// Mirrors `selectInfluencerIdentitiesSortByOptions` pattern from campaigns/selectors —
// sort options are declared by the BE on the HATEOAS link itself.
export const selectAgentSourcedCreatorsSortByOptions = (
  state: IApplicationState,
  campaignId: number,
): string[] => {
  const campaign = selectCampaign(state, campaignId)
  const link = campaign?.links?.find((l) => l.rel === VIBE_CHECK_LIST_REL)
  const fromLink = (link?.sort_by ?? []) as string[]
  return fromLink.length > 0 ? fromLink : FALLBACK_SORT_OPTIONS
}
